import Logo from "./Logo";
import UserMenu from "./UserMenu";
import NotificationBell from "./NotificationBell";
import { PlatformSwitcher } from "./PlatformSwitcher";
import CartIcon from "./cart/CartIcon";
import CartDrawer from "./cart/CartDrawer";
// Deep imports keep the home critical path off the lucide barrel chunk.
import Menu from "lucide-react/dist/esm/icons/menu";
import X from "lucide-react/dist/esm/icons/x";
import Shield from "lucide-react/dist/esm/icons/shield";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
} from "./ui/navigation-menu";
import { cn } from "@/lib/utils";
import { useNavCTA } from "@/hooks/useNavCTA";
import { preserveParams } from "@/lib/utmPreserver";
import { useExternalAnalytics } from "@/hooks/useExternalAnalytics";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, hasRole } = useAuth();
  const isAdmin = hasRole("admin");
  const { isLoggedIn } = useNavCTA();
  const { trackBoth } = useExternalAnalytics();

  // Fires synchronously in the same click tick so GA4's beacon transport
  // flushes before navigation — works for same-tab, Cmd/Ctrl-click, and
  // middle-click (via onAuxClick).
  const trackHeaderCTA = (cta_id: string, destination: string) => {
    trackBoth("header_cta_click", {
      cta_id,
      destination,
      surface: "header",
      logged_in: false,
    });
  };

  // Product-app nav. Marketing surfaces (personas, pricing pitch,
  // founder pilot, blog) live on castready.org. Hub header offers only
  // entry points into the product itself.
  const simpleNavItems: Array<{ label: string; href: string; external?: boolean; emphasis?: "primary" | "secondary" }> = [
    { label: "Product demos", href: "/demo" },
    { label: "Training Kits", href: "/training-kits" },
    { label: "Pricing", href: "/pricing" },
    { label: "Status", href: "/status" },
    { label: "Support", href: "/support" },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50">
      <div className="container mx-auto px-6 py-4 flex items-center justify-between">
        <Logo />

        {/* Desktop Navigation - 6 items max */}
        <NavigationMenu className={cn("hidden lg:flex", user && "lg:hidden")}>
          <NavigationMenuList>
            {/* Simple nav items */}
            {simpleNavItems.map((item) => (
              <NavigationMenuItem key={item.label}>
                <NavigationMenuLink asChild>
                  {item.external ? (
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener"
                      className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors px-4 py-2 focus-visible:ring-2 focus-visible:ring-primary rounded-md"
                    >
                      {item.label}
                    </a>
                  ) : (
                    <Link
                      to={item.href}
                      className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors px-4 py-2 focus-visible:ring-2 focus-visible:ring-primary rounded-md"
                    >
                      {item.label}
                    </Link>
                  )}
                </NavigationMenuLink>
              </NavigationMenuItem>
            ))}
          </NavigationMenuList>
        </NavigationMenu>

        <div className="flex items-center gap-2">
          {/* Platform Switcher - only show when logged in */}
          {user && <PlatformSwitcher />}
          
          {user && <NotificationBell />}

          <CartIcon />

          {/* CTAs for non-logged-in users — Coach-first primary, actor/parent secondary */}
          {!isLoggedIn && (
            <>
              <Button variant="ghost" size="sm" asChild className="hidden md:flex">
                 {/*<Link
                  to={preserveParams("/onboarding?role=family")}
                  onClick={() => trackHeaderCTA("header_family", "/onboarding?role=family")}
                  onAuxClick={() => trackHeaderCTA("header_family", "/onboarding?role=family")}
                >
                  I'm an actor/parent
                </Link>*/}
                 <Link
                  to={preserveParams("/#platform-callback-zone")}
                  onClick={() => trackHeaderCTA("header_family", "/#platform-callback-zone")}
                  onAuxClick={() => trackHeaderCTA("header_family", "/#platform-callback-zone")}
                >
                  I'm an actor/parent
                </Link>
              </Button>
              <Button size="sm" asChild className="hidden sm:flex">
                 {/*<Link
                  to={preserveParams("/onboarding?role=studio")}
                  onClick={() => trackHeaderCTA("header_studio", "/onboarding?role=studio")}
                  onAuxClick={() => trackHeaderCTA("header_studio", "/onboarding?role=studio")}
                >
                  Sign up as Studio/Coach
                </Link>*/}
                  <Link
                   to={preserveParams("/#platform-coach")}
                   onClick={() => trackHeaderCTA("header_studio", "/#platform-coach")}
                   onAuxClick={() => trackHeaderCTA("header_studio", "/#platform-coach")}
                  >
                    Sign up as Studio/Coach
                  </Link> 
              </Button>
            </>
          )}

          {/* Account link for logged-in users */}
          {user && (
            <Button variant="ghost" size="sm" asChild className="hidden sm:flex">
              <Link to="/account" className="gap-2">
                Account
              </Link>
            </Button>
          )}

          {isAdmin && (
            <Button variant="ghost" size="sm" asChild className="hidden sm:flex">
              <Link to="/admin" className="gap-2">
                <Shield className="w-4 h-4" />
                Admin
              </Link>
            </Button>
          )}

          <UserMenu />

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2 text-foreground focus-visible:ring-2 focus-visible:ring-primary rounded-md"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-expanded={mobileMenuOpen}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && !user && (
        <div className="lg:hidden bg-card border-t border-border">
          <nav className="container mx-auto px-6 py-4 flex flex-col gap-2">
            {/* Simple nav items */}
            {simpleNavItems.map((item) => (
              item.external ? (
                <a
                  key={item.label}
                  href={item.href}
                  target="_blank"
                  rel="noopener"
                  className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors py-2 block focus-visible:ring-2 focus-visible:ring-primary rounded-md"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.label}
                </a>
              ) : (
                <Link
                  key={item.label}
                  to={item.href}
                  className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors py-2 block focus-visible:ring-2 focus-visible:ring-primary rounded-md"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.label}
                </Link>
              )
            ))}
            
            {/* Mobile Account & Admin Links */}
            {user && (
              <Link
                to="/account"
                className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors py-2 block focus-visible:ring-2 focus-visible:ring-primary rounded-md"
                onClick={() => setMobileMenuOpen(false)}
              >
                Account
              </Link>
            )}
            {isAdmin && (
              <Link
                to="/admin"
                className="text-sm font-medium text-primary hover:text-primary/80 transition-colors py-2 flex items-center gap-2 focus-visible:ring-2 focus-visible:ring-primary rounded-md"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Shield className="w-4 h-4" />
                Admin Dashboard
              </Link>
            )}

            {/* Mobile CTAs — Coach-first */}
            <div className="pt-4 border-t border-border flex flex-col gap-2">
              <Button asChild className="w-full">
                 {/*<Link
                    to={preserveParams("/onboarding?role=studio")}
                    onClick={() => {
                    trackHeaderCTA("header_studio_mobile", "/onboarding?role=studio");
                    setMobileMenuOpen(false);
                  }}
                  onAuxClick={() =>
                   trackHeaderCTA("header_studio_mobile", "/onboarding?role=studio")
                  }
                >
                  Sign up as Studio/Coach or Talent Rep
                </Link>*/}
                  <Link
                   to={preserveParams("/#platform-coach")}
                   onClick={() => {
                   trackHeaderCTA("header_studio_mobile", "/#platform-coach");
                   setMobileMenuOpen(false);
                    }}
                    onAuxClick={() =>
                    trackHeaderCTA("header_studio_mobile", "/#platform-coach")
                    }
                  >    
                    Sign up as Studio/Coach or Talent Rep
                  </Link>  
              </Button>
              <Button variant="ghost" asChild className="w-full">
                {/*<Link
                  to={preserveParams("/onboarding?role=family")}
                  onClick={() => {
                    trackHeaderCTA("header_family_mobile", "/onboarding?role=family");
                    setMobileMenuOpen(false);
                  }}
                  onAuxClick={() =>
                    trackHeaderCTA("header_family_mobile", "/onboarding?role=family")
                  }
                >
                  I'm an actor or parent → Callback Zone
                </Link>*/}
                <Link
                 to={preserveParams("/#platform-callback-zone")}
                 onClick={() => {
                 trackHeaderCTA("header_family_mobile", "/#platform-callback-zone");
                 setMobileMenuOpen(false);
                  }}
                  onAuxClick={() =>
                    trackHeaderCTA("header_family_mobile", "/#platform-callback-zone")
                  }
                >
                  I'm an actor or parent → Callback Zone
                </Link>
              </Button>
            </div>
          </nav>
        </div>
      )}

      {/* Mobile Navigation — authenticated (minimal) */}
      {mobileMenuOpen && user && (
        <div className="lg:hidden bg-card border-t border-border">
          <nav className="container mx-auto px-6 py-4 flex flex-col gap-2">
            <Link to="/" onClick={() => setMobileMenuOpen(false)} className="text-sm font-medium py-2 text-foreground">Home</Link>
            <Link to="/callback-zone" onClick={() => setMobileMenuOpen(false)} className="text-sm font-medium py-2 text-foreground">Callback Zone</Link>
            <Link to="/castings" onClick={() => setMobileMenuOpen(false)} className="text-sm font-medium py-2 text-foreground">Castings</Link>
            {hasRole("coach") && (
              <Link to="/coach" onClick={() => setMobileMenuOpen(false)} className="text-sm font-medium py-2 text-foreground">Coach</Link>
            )}
            {(hasRole("agent") || hasRole("manager") || hasRole("casting_professional") || hasRole("producer") || hasRole("studio") || hasRole("coach")) && (
              <Link to="/pro" onClick={() => setMobileMenuOpen(false)} className="text-sm font-medium py-2 text-foreground">Pro</Link>
            )}
            <Link to="/account" onClick={() => setMobileMenuOpen(false)} className="text-sm font-medium py-2 text-foreground">Account</Link>
            <Link to="/support" onClick={() => setMobileMenuOpen(false)} className="text-sm font-medium py-2 text-foreground">Help</Link>
            {isAdmin && (
              <Link to="/admin" onClick={() => setMobileMenuOpen(false)} className="text-sm font-medium py-2 flex items-center gap-2 text-primary">
                <Shield className="w-4 h-4" /> Admin
              </Link>
            )}
          </nav>
        </div>
      )}

      <CartDrawer />
    </header>
  );
};

export default Header;
